import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Upload, Camera, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { photoUrl } from "@/lib/format";
import { Logo, KenyaStripe } from "@/components/Logo";

const EMPTY = {
  name: "", email: "", phone: "", date_of_birth: "", city: "", country: "Kenya",
  gender: "male", rating: 4.0, seed_level: 5, category: "C", is_international: false,
  achievements: "", bio: "",
  video_url_1: "", video_url_2: "", video_url_3: "",
  instagram: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  payment_method: "", payment_reference: "",
  photo_path: null,
};

export default function RegisterPlayer() {
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [cfg, setCfg] = useState(null);

  React.useEffect(() => {
    api.get("/public/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Image files only");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/registrations/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_path", data.photo_path);
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  const submit = async () => {
    if (!form.photo_path) return toast.error("Profile photo is required");
    if (!form.name || !form.email || !form.phone) return toast.error("Name, email and phone are required");
    setSubmitting(true);
    try {
      await api.post("/registrations", form);
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 score-grid-bg">
        <Card className="max-w-lg w-full p-10 bg-[#11241b] border-white/10 text-center" data-testid="register-done">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#00A86B] mb-4" />
          <div className="font-anton text-4xl uppercase tracking-tight text-white">Application Received</div>
          <p className="text-zinc-400 mt-2">Your registration has been submitted to the league office. You will be contacted once approved.</p>
          <div className="mt-6 flex justify-center gap-2">
            <Button asChild className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold"><Link to="/live">View Live Broadcast</Link></Button>
            <Button asChild variant="outline" className="border-white/20 text-white"><Link to="/admin">Operator Login</Link></Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen score-grid-bg py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/live" className="inline-flex items-center text-zinc-400 hover:text-white text-sm mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Live Broadcast
        </Link>
        <div className="mb-8 flex items-center gap-5">
          <Logo className="h-16 w-auto" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Player Application · Season 03</div>
            <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Register</h1>
            <KenyaStripe className="mt-2 w-32" />
          </div>
        </div>
        <p className="text-zinc-400 mb-6 max-w-2xl">Submit your application to enter the Mara Padel auction pool. Profile photo is mandatory; match videos help owners scout your play.</p>

        <Card className="bg-[#11241b] border-white/10 p-6 md:p-8">
          {/* Photo */}
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Profile Photo *</div>
            <div className="flex items-center gap-4">
              <div className="w-32 h-40 rounded-lg bg-[#060d09] border-2 border-dashed border-white/10 overflow-hidden flex items-center justify-center">
                {form.photo_path ? (
                  <img src={photoUrl(form.photo_path)} alt="Preview" className="w-full h-full object-cover" data-testid="register-photo-preview" />
                ) : (
                  <Camera className="w-8 h-8 text-zinc-700" />
                )}
              </div>
              <div>
                <label className="inline-flex">
                  <input data-testid="register-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                  <span className="cursor-pointer inline-flex items-center px-4 py-2 rounded-md bg-[#00A86B] text-white font-bold text-sm uppercase tracking-widest hover:bg-[#00935A]">
                    <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : form.photo_path ? "Replace Photo" : "Upload Photo"}
                  </span>
                </label>
                <div className="text-xs text-zinc-500 mt-2">JPG / PNG · Max 5MB · Required</div>
              </div>
            </div>
          </div>

          {/* Identity */}
          <Section title="Identity & Contact">
            <Field label="Full Name *"><Input data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Email *"><Input data-testid="register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Phone *"><Input data-testid="register-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#060d09] border-white/10 text-white" placeholder="+254 …" /></Field>
            <Field label="Date of Birth"><Input data-testid="register-dob" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Gender">
              <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="register-gender" className="bg-[#060d09] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white">
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="City"><Input data-testid="register-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Country"><Input data-testid="register-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Instagram Handle"><Input data-testid="register-instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} className="bg-[#060d09] border-white/10 text-white" placeholder="@yourhandle" /></Field>
          </Section>

          {/* Play Profile */}
          <Section title="Play Profile">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="register-category" className="bg-[#060d09] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white">
                  {["A","B","C","D","E"].map(c => <SelectItem key={c} value={c}>Category {c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Self-Rating (1.0 – 5.0)"><Input data-testid="register-rating" type="number" step="0.1" min="1" max="5" value={form.rating} onChange={(e) => set("rating", parseFloat(e.target.value) || 0)} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Preferred Seed Level"><Input data-testid="register-seed" type="number" value={form.seed_level} onChange={(e) => set("seed_level", parseInt(e.target.value) || 0)} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <div className="flex items-center gap-2 mt-6">
              <Switch data-testid="register-international" checked={form.is_international} onCheckedChange={(v) => set("is_international", v)} />
              <Label className="text-zinc-300">International Player</Label>
            </div>
          </Section>

          {/* Match Videos */}
          <Section title="Match Videos (YouTube / Vimeo / public links)">
            <Field label="Video Link 1" full><Input data-testid="register-video-1" value={form.video_url_1} onChange={(e) => set("video_url_1", e.target.value)} className="bg-[#060d09] border-white/10 text-white" placeholder="https://youtu.be/…" /></Field>
            <Field label="Video Link 2" full><Input data-testid="register-video-2" value={form.video_url_2} onChange={(e) => set("video_url_2", e.target.value)} className="bg-[#060d09] border-white/10 text-white" placeholder="Optional" /></Field>
            <Field label="Video Link 3" full><Input data-testid="register-video-3" value={form.video_url_3} onChange={(e) => set("video_url_3", e.target.value)} className="bg-[#060d09] border-white/10 text-white" placeholder="Optional" /></Field>
          </Section>

          {/* Achievements & Bio */}
          <Section title="Achievements & Bio">
            <Field label="Achievements" full><Textarea data-testid="register-achievements" rows={4} value={form.achievements} onChange={(e) => set("achievements", e.target.value)} placeholder="Tournament wins, rankings, titles…" className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Short Bio" full><Textarea data-testid="register-bio" rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Tell scouts about your game style and experience" className="bg-[#060d09] border-white/10 text-white" /></Field>
          </Section>

          {/* Emergency Contact */}
          <Section title="Emergency Contact">
            <Field label="Name"><Input data-testid="register-emergency-name" value={form.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Phone"><Input data-testid="register-emergency-phone" value={form.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} className="bg-[#060d09] border-white/10 text-white" /></Field>
          </Section>

          {/* Registration Fee */}
          <div className="mb-6 p-5 rounded-xl border border-[#E32424]/40 bg-gradient-to-br from-[#BB0000]/10 via-transparent to-[#00A86B]/10" data-testid="register-fee-block">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E32424] font-bold">Registration Fee</div>
            <div className="mt-2 flex items-baseline justify-between gap-3 flex-wrap">
              <div className="font-anton text-4xl text-white">
                KES {(cfg?.player_registration_fee ?? 5000).toLocaleString()}
              </div>
              <div className="text-xs text-zinc-300 max-w-md">
                One-time league registration fee. <span className="text-[#00A86B] font-semibold">If you remain unsold at auction, your fee is refunded in full.</span>
              </div>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Payment Method</label>
                <select data-testid="register-payment-method" value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} className="w-full mt-1 bg-[#060d09] border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                  <option value="">Select…</option>
                  <option value="MPESA">M-PESA</option>
                  <option value="BANK">Bank Transfer</option>
                  <option value="CASH">Cash (at venue)</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Payment Reference / Transaction ID</label>
                <Input data-testid="register-payment-ref" value={form.payment_reference} onChange={(e) => set("payment_reference", e.target.value)} placeholder="e.g. MPESA QH7K3..." className="bg-[#060d09] border-white/10 text-white" />
              </div>
            </div>
            <div className="mt-3 text-[11px] text-zinc-400">League finance will verify your payment within 48 hours. You may also pay at the venue and submit the reference later.</div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button data-testid="btn-submit-registration" onClick={submit} disabled={submitting} className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold h-12 px-8 uppercase tracking-widest">
              {submitting ? "Submitting…" : "Submit Application"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#00A86B] mb-3">{title}</div>
      <div className="grid md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
